import { useState, useCallback, useRef } from "react";
import { URL_CONSTANTS } from "../../axios/urlLinksThemes";
import { I_EXTERNAL_PAGES, IRESAllData } from "../../shared/commonUtils";
import { httpPost } from "../../axios/axiosUtils";
import {
  getBussinessData,
  getUserPrefersLang,
} from "../../shared/helpersMethod";
import { useNavigate } from "react-router-dom";
import useGetToken from "../useGetToken";

const useGetExternalPages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState<I_EXTERNAL_PAGES>();
  const { getToken } = useGetToken();
  const userLanguage = getUserPrefersLang();
  const queryParamLang = `?lang=${userLanguage}`;

  // Prevent multiple simultaneous API calls
  const apiCallInProgress = useRef(false);

  const getPageBySlug = useCallback(
    async (
      pageSlug: string | undefined,
      retryAfterTokenRefresh = false
    ): Promise<void> => {
      if (!pageSlug) {
        console.error("Page slug is required");
        return;
      }

      // Prevent multiple simultaneous calls (but allow retry after token refresh)
      if (apiCallInProgress.current && !retryAfterTokenRefresh) {
        console.log("API call already in progress, skipping");
        return;
      }

      // Get business token
      let bussinessData = getBussinessData();
      if (!bussinessData?.accessToken) {
        console.log("No business token found, attempting to get token");
        try {
          const newToken = await getToken();
          // Update business data after getting new token
          bussinessData = getBussinessData();
          if (!bussinessData?.accessToken) {
            console.error("Still no token after getToken call");
            return;
          }
        } catch (error) {
          console.error("Failed to get token:", error);
          return;
        }
      }

      apiCallInProgress.current = true;
      setLoading(true);

      try {
        console.log(`🚀 Making API call to get external page: ${pageSlug}`);
        const res: IRESAllData = await httpPost(
          URL_CONSTANTS.get("URL_EXTERNAL_PAGES")! + `${pageSlug}`,
          { lang: userLanguage },
          {},
          bussinessData.accessToken
        );

        if (res.type === "error" && res?.token === "expired") {
          console.log("🔄 Token expired, refreshing token and retrying");
          try {
            // Reset the API call flag to allow retry
            apiCallInProgress.current = false;

            const newToken = await getToken(true); // Force token refresh
            console.log(
              "✅ New token obtained:",
              newToken ? "Success" : "Failed"
            );

            // Retry the API call with new token
            if (newToken) {
              console.log("🔄 Retrying external page API call with new token");
              return getPageBySlug(pageSlug, true); // Pass true to indicate this is a retry
            } else {
              throw new Error("Failed to get new token");
            }
          } catch (tokenError) {
            console.error("❌ Failed to refresh token:", tokenError);
            throw tokenError;
          }
        }

        if (res.data && res.type !== "error") {
          console.log("✅ Successfully fetched external page data");
          setPageInfo(res.data);
        } else if (res.type === "error" && res.token !== "expired") {
          console.error("❌ Page not found, navigating to 404");
          navigate("/404");
        } else {
          console.error("❌ API returned error:", res);
        }
      } catch (error: any) {
        console.error("❌ API call failed:", error);
        // Navigate to 404 on network or other errors
        navigate("/404");
      } finally {
        setLoading(false);
        apiCallInProgress.current = false;
      }
    },
    [getToken, userLanguage, navigate]
  );

  return {
    getPageBySlug,
    pageInfo,
    loading,
  };
};

export default useGetExternalPages;
